<template>
  <fieldset>
      <div id="vdsModel-wp" class="tla-form-group">
        <label class="tla-form-group__label tla-form-group__label--required" :class="{ 'sr-only' : hideLabels }" v-html="label"></label>

        <select
          id="modelId"
          class="tla-form-group__input tla-form-group__input--arrow"
          :class="getModelValidationClass('input')"
          v-validate.continues="'required|min:1'"
          :data-vv-name="modelValidationTag"
          :data-vv-scope="validationScope"
          v-model="modelId"
          :disabled="disableModelInput"
          name="vdsModelIds">
          <option disabled selected value="null">
            {{placeholder}}
          </option>
          <option
            v-for="model in models"
            :value="model.modelId"
            :key="model.modelId">
            {{model.modelName}}
          </option>
        </select>
        <span class="tla-form-group__message tla-form-group__message--spaced" :class="getModelValidationClass('message')">Please select a model.</span>
      </div>
    </fieldset>
</template>

<script lang="ts">
import { Validator } from 'vee-validate';
import {
  Component,
  Prop,
  Vue,
  Watch,
  Inject
} from 'vue-property-decorator';
import VueEvents from 'vue-events';
import IDerivative from '@/models/vehicle/interfaces/derivative.d';
import { validationService } from '../../services/ValidationService';
import SearchQuery from '../../models/SearchQuery';
import IModel from '../../models/vehicle/interfaces/model.d';

Vue.use(VueEvents);

@Component({})
export default class VehicleModelFilterComponent extends Vue {
  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: '' })
  public moduleKey!: string;

  @Prop({ default: false })
  public submitDerivativeOnSelection!: boolean;

  @Prop({ default: 'Style' })
  public label!: string;

  @Prop({ default: 'Please choose a style' })
  public placeholder!: string;

  @Prop({ default: 'vehicleSelectionValidation' })
  public fieldValidationName!: string;

  @Prop({ default: null })
  public validationScope!: string;

  @Prop({ default: false })
  public hideLabels!: boolean;

  public modelValidationTag: string = 'modelTag';

  public get searchQuery(): SearchQuery {
    return this.$store.getters[`searchQueryModule${this.moduleKey}/searchQuery`];
  }

  public async submitDerivative(derivative: IDerivative | null) {
    await this.$store.dispatch(`vehicleModule${this.moduleKey}/submitDerivative`, derivative);
  }

  public async submitModelId(modelId: number | null) {
    await this.$store.dispatch(`vehicleModule${this.moduleKey}/submitModelId`, modelId);
  }

  public async updateFilteredResults() {
    await this.$store.dispatch(`vehicleSearchModule${this.moduleKey}/updateFilteredResults`);
  }

  public get models(): IModel[] {
    return this.$store.getters[`vehicleSearchModule${this.moduleKey}/models`];
  }

  public get disableModelInput(): boolean {
    return this.rangeId === null || this.models === null || this.models.length === 0;
  }

  public getModelValidationClass(fieldType: string) {
    if (this.modelId !== null && this.modelId > 0) {
      return '';
    }

    return this.getValidationClass(`${this.fieldValidationName}${this.moduleKey}`, fieldType);
  }

  public getValidationClass(fieldName: string, fieldType: string) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, false, false, this.validationScope);
  }

  public async submitResetVehicleCustomisationData() {
    await this.$store.dispatch(`vehicleModule${this.moduleKey}/submitResetVehicleCustomisationData`);
  }

  get rangeId(): number | null {
    return this.searchQuery.rangeIds;
  }

  get modelId(): number | null {
    return this.searchQuery.modelId;
  }

  set modelId(modelId: number | null) {
    this.updateModelId(modelId);
    this.validateComponent();
  }

  public async updateModelId(modelId: number | null) {
    await this.submitResetVehicleCustomisationData();
    await this.submitModelId(modelId);
    await this.updateFilteredResults();

    if (this.submitDerivativeOnSelection) {
      if (modelId === null) {
        await this.submitDerivative(null);
      } else {
        const derivatives = this.$store.getters[`vehicleSearchModule${this.moduleKey}/derivatives`].filter((x) => x.modelId === this.modelId);
        await this.submitDerivative(derivatives && derivatives.length > 0 ? derivatives[0] : null);
      }
    }

    this.$events.emit('vehicleLevelSet', this.moduleKey);
  }

  public validateComponent() {
    this.$validator.validateAll(this.validationScope);
  }

  public async submitResetResultsAndFilteredResults() {
    await this.$store.dispatch(`vehicleSearchModule${this.moduleKey}/submitResetResultsAndFilteredResults`);
  }

  public async rangeChanged() {
    if (this.models !== null && this.models.length === 1) {
      this.updateModelId(this.models[0].modelId);
      this.updateFilteredResults();
    } else {
      this.updateModelId(null);
    }
  }

  @Watch('modelId')
  public modelIdChanged() {
    if (this.modelId === null && this.models.length === 1) {
      this.updateModelId(this.models[0].modelId);
    }
  }

  public async created() {
    this.$events.on('vehicleRangeChanged', (moduleKey) => this.rangeChanged());
    this.rangeChanged();
  }
}
</script>
